import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Pagination,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import strings from "global/constants/StringConstants";
import { debounceEventHandler } from "helpers/methods";
import { AppModal } from "global/components/AppModal";
import styles from "./CloudStoragePicker.styles";
import { CloudFile, CloudProvider } from "./types";
import EmptyState from "global/components/EmptyState/EmptyState";

interface CloudFilesDialogProps {
  open: boolean;
  onClose: () => void;
  provider: CloudProvider;
  files: CloudFile[];
  onSearchChange: (term: string) => void;
  onOpenFolder: (file: CloudFile) => void;
  onGoBack?: () => void;
  showBackButton?: boolean;
  onUpload: (file: CloudFile) => void;
  itemsPerPage?: number;
}

const CloudFilesDialog = ({
  open,
  onClose,
  provider,
  files,
  onSearchChange,
  onOpenFolder,
  onGoBack,
  showBackButton,
  onUpload,
  itemsPerPage = 8,
}: CloudFilesDialogProps) => {
  const [selectedFile, setSelectedFile] = useState<CloudFile | null>(null);
  const [page, setPage] = useState(1);

  const paginated = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return files.slice(start, start + itemsPerPage);
  }, [files, page, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(files.length / itemsPerPage));

  const handleRowClick = (file: CloudFile) => {
    if (file.type === "folder") {
      setSelectedFile(null);
      setPage(1);
      onOpenFolder(file);
      return;
    }
    setSelectedFile((prev) => (prev?.id === file.id ? null : file));
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPage(1);
    onClose();
  };

  const handleUploadClick = () => {
    if (selectedFile && selectedFile.type !== "folder") {
      onUpload(selectedFile);
      setSelectedFile(null);
      setPage(1);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title={provider.displayName}
      subtitle="Select a file to upload"
      // The provider's own mark sits in the shell's icon tile; `neutral` gives
      // it a pale, colour-agnostic ground so no logo clashes with the tint.
      icon={
        <Box
          component="img"
          src={provider.icon}
          alt=""
          sx={{ width: 22, height: 22, objectFit: "contain" }}
        />
      }
      tone="neutral"
      // A file picker — the design system's L width.
      size="lg"
      // The list below runs its own scroller with a fixed min/max height.
      disableBodyPadding
      scrollBody={false}
      footerStart={
        files.length > 0 ? (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            size="small"
            shape="rounded"
          />
        ) : undefined
      }
      footer={
        <>
          {selectedFile && selectedFile.type !== "folder" && (
            <Chip
              label={selectedFile.name}
              size="small"
              onDelete={() => setSelectedFile(null)}
              sx={styles.selectedChip}
            />
          )}
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedFile || selectedFile.type === "folder"}
            onClick={handleUploadClick}
          >
            Upload
          </Button>
        </>
      }
    >
      {/* Toolbar */}
      <Box sx={styles.toolbar}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search files"
          onChange={debounceEventHandler(
            (e: React.ChangeEvent<HTMLInputElement>) =>
              onSearchChange(e.target.value),
            strings.SEARCH_TIME_OUT,
          )}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": "Search files" }}
          sx={styles.searchField}
        />
        {showBackButton && onGoBack && (
          <Box
            component="button"
            type="button"
            sx={styles.breadcrumb}
            onClick={onGoBack}
          >
            <ArrowBackIcon fontSize="small" />
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              {strings.BACK_TO_HOME}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Body */}
      <Box sx={styles.listContainer}>
        {files.length === 0 ? (
          <EmptyState
            size="medium"
            icon="folder"
            title="No files here yet"
            body="This folder is empty. Try a different search term, or go back to browse other folders."
          />
        ) : (
          <Stack
            sx={styles.fileList}
            role="group"
            aria-label={`${provider.displayName} files`}
          >
            {paginated.map((file) => {
              const isFolder = file.type === "folder";
              const isSelected = selectedFile?.id === file.id;
              return (
                <Box
                  key={file.id}
                  sx={styles.fileRow(isSelected)}
                  onClick={() => handleRowClick(file)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isFolder ? undefined : isSelected}
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleRowClick(file);
                    }
                  }}
                >
                  <Box sx={styles.fileIconWrap(isFolder)}>
                    {isFolder ? (
                      <FolderOpenRoundedIcon sx={{ fontSize: 22 }} />
                    ) : (
                      <InsertDriveFileRoundedIcon sx={{ fontSize: 22 }} />
                    )}
                  </Box>
                  <Box sx={styles.fileMeta}>
                    <Typography sx={styles.fileName} title={file.name}>
                      {file.name}
                    </Typography>
                    <Typography sx={styles.fileType}>
                      {isFolder ? "Folder" : "PDF document"}
                    </Typography>
                  </Box>
                  {isFolder ? (
                    <ChevronRightRoundedIcon sx={styles.chevron} />
                  ) : isSelected ? (
                    <CheckCircleRoundedIcon sx={styles.checkIcon} />
                  ) : (
                    <Box sx={styles.checkPlaceholder} />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </AppModal>
  );
};

export default CloudFilesDialog;
